/**
 * Compose block types -- JSON contracts for ControlRenderer composition.
 *
 * Primitive blocks (form, table, view) wrap existing schema files.
 * Composer blocks (tab, wizard) nest child blocks by URI or inline object.
 * ComposeSchema is the root entry passed to ControlRenderer.
 */

import { normalizeFormEngineMode } from '../constants/formMode';

export type BlockType = 'form' | 'table' | 'view' | 'tab' | 'wizard' | 'stack' | 'action' | 'accordion' | 'button' | 'slot';

export interface BaseBlock {
  $type: BlockType;
  $id: string;
  semanticId?: string;
  title?: string;
  wire?: import('@packages/block-events/core').BlockWire;
}

export interface TableBlock extends BaseBlock {
  $type: 'table';
  schema?: string;
  namespace?: string;
  /** Pre-fetched data from a builder action — renders static rows, skips API pagination. */
  data?: unknown[];
  columns?: Array<{ key: string; label: string; [k: string]: unknown }>;
  /**
   * Runtime path-parameter context forwarded to DynamicTableV2.
   * Values here substitute `{Token}` placeholders in the schema endpoint via `pathParams`.
   * Example: `context: { particiantId: 'abc-123' }` resolves `{particiantId}` in the URL.
   */
  context?: Record<string, unknown>;
}

export interface ViewBlockDataSource {
  fetch: {
    actionId: string;
    params?: Record<string, string>;
  };
  loadingPolicy?: 'wait-root' | 'lazy';
}

export interface ViewBlock extends BaseBlock {
  $type: 'view';
  schema?: { domain: string; orch: string };
  /**
   * Simple action ID for self-fetch. The renderer auto-constructs `{ id: params.id }` params.
   * Preferred over `dataSource` — simpler, same result.
   */
  fetchActionId?: string;
  /** @deprecated Use `fetchActionId` instead. Kept for backward compat (wizard context). */
  dataSource?: ViewBlockDataSource;
  skeletonOnFetch?: boolean;
  presentation?: 'default' | 'surface';
  surfacePadding?: string;
  /**
   * Pre-fetched data from a builder action — hydrates instantly on first paint.
   * When a `fetchActionId` (block-level or schema-level) is also present, the view
   * subscribes for cache invalidation and re-fetches after sibling edits.
   */
  data?: Record<string, unknown>;
}

export interface TabEntry {
  id: string;
  label: string;
  block: string | AnyBlock;
  /** When true, the tab content unmounts when hidden and remounts fresh when re-visited. */
  remountOnSwitch?: boolean;
}

export interface TabBlock extends BaseBlock {
  $type: 'tab';
  tabs: TabEntry[];
  tabsAlign?: 'start' | 'center';
  /** ID of the tab that should be active on first render. Defaults to the first tab. */
  defaultTab?: string;
  /**
   * Tab IDs that should be pre-rendered on mount (hidden, not active).
   * Use when a background tab needs to load data immediately — e.g. to populate
   * a badge count — without waiting for the user to visit it.
   */
}

export interface AccordionSection {
  id: string;
  label: string;
  block: string | AnyBlock;
}

export interface AccordionBlock extends BaseBlock {
  $type: 'accordion';
  sections: AccordionSection[];
  /** Allow multiple sections open at once (default: single). */
  accordionType?: 'single' | 'multiple';
  /** Stack sections in one bordered shell vs separate cards. */
  grouped?: boolean;
}

// --- Wizard orchestration types ---

export type ValidationScope = 'active' | 'all' | 'none';

export interface StepSubmitConfig {
  actionId: string;
  params?: Record<string, string>;
  extractMeta?: Record<string, string>;
}

export interface WizardConditionalNextStep {
  condition: WizardCondition;
  then: string;
  else: string;
}

export interface StepSecondaryAction {
  label: string;
  submit?: boolean;
  exitAfter?: boolean;
}

export interface StepTransition {
  validate?: boolean;
  validationScope?: ValidationScope;
  submit?: StepSubmitConfig;
  /**
   * When false, the wizard will NOT trigger the form's own lifecycle submit
   * even if `transition.submit` is absent. Defaults to true.
   */
  submitForm?: boolean;
  /**
   * Extract meta from the form's own submit result (form-owns-submit mode).
   * Only used when `submit` is absent and the form's lifecycle returns a result.
   */
  extractMeta?: Record<string, string>;
  label?: string;
  nextStep?: string | WizardConditionalNextStep;
  secondaryAction?: StepSecondaryAction;
}

export interface WizardCondition {
  field: string;
  eq?: unknown;
  neq?: unknown;
  in?: unknown[];
  notIn?: unknown[];
}

export interface WizardContext {
  dataSource?: ViewBlockDataSource;
  initialMeta?: Record<string, unknown>;
}

export interface WizardCancelConfig {
  actionId?: string;
}

export type FormStepMode = 'internal' | 'exposed';

export interface WizardStep {
  id: string;
  label: string;
  block: string | AnyBlock;
  transition?: StepTransition;
  guard?: WizardCondition;
  /** v2 — per-step button overrides */
  buttons?: WizardStepButtons;
  /** v2 — visibility expression */
  visible?: string;
}

export interface WizardStepButtons {
  primary?: { label?: string; hidden?: boolean };
  back?: { label?: string; hidden?: boolean };
  cancel?: { label?: string; hidden?: boolean; skipConfirm?: boolean };
  next?: { label?: string };
  secondary?: Array<{ id: string; label: string; variant?: string; visible?: string }>;
}

export interface WizardBlock extends BaseBlock {
  $type: 'wizard';
  steps: WizardStep[];
  context?: WizardContext;
  onComplete?: { actionId: string };
  onCancel?: WizardCancelConfig;
  /** v2 — single action handler for all wizard events */
  handler?: string;
  /** v2 — wizard-level button defaults */
  buttons?: WizardStepButtons;
}

// --- FormBlock extension for wizard integration ---

/**
 * Form rendering mode for compose `FormBlock`.
 *
 * - `create` / `edit` / `view` — passed through to FormRenderer as-is.
 * - `upsert` — resolves to `edit` when an entity id exists, otherwise `create`.
 * - `undefined` — same as `upsert` (auto-resolve from entity ID).
 *
 * Callers only need to pass `'view'` explicitly; create vs edit is derived
 * from the presence of `entityId` or `params.id`.
 */
export type FormBlockMode = 'create' | 'edit' | 'view' | 'upsert';

/**
 * Derive the effective FormRenderer mode for a form block.
 *
 * When `mode` is `undefined` or `'upsert'`, auto-resolves:
 *   - `entityId` / `params.id` present → `'edit'`
 *   - otherwise → `'create'`
 */
export function resolveFormBlockMode(
  mode: FormBlockMode | undefined,
  params?: Record<string, string | undefined>,
  entityId?: string,
): Exclude<FormBlockMode, 'upsert'> {
  if (mode != null && mode !== 'upsert') {
    return mode as Exclude<FormBlockMode, 'upsert'>;
  }
  const id = entityId ?? params?.id;
  return id != null && id !== '' ? 'edit' : 'create';
}

/** ComposeModal meta.mode — prefer `create`; `add` is legacy alias (see formMode.ts). */
export type ComposeModalMetaMode = FormBlockMode | 'add';

function normalizeComposeMetaMode(metaMode: unknown): FormBlockMode | undefined {
  if (metaMode === 'upsert') {
    return metaMode;
  }
  const normalized = normalizeFormEngineMode(metaMode);
  if (normalized != null) {
    return normalized;
  }
  return undefined;
}

/**
 * Prefer live `meta.mode` (ComposeModal in-place view→edit) over the static
 * `block.mode` baked into the builder result.
 */
export function resolveLiveFormBlockMode(
  blockMode: FormBlockMode | undefined,
  meta: Record<string, unknown> | undefined,
  params?: Record<string, string | undefined>,
  entityId?: string,
): Exclude<FormBlockMode, 'upsert'> {
  const metaMode = normalizeComposeMetaMode(meta?.mode);
  return resolveFormBlockMode(metaMode ?? blockMode, params, entityId);
}

/**
 * Stable dependency key for ActionBlockRenderer builder re-runs.
 * Excludes `mode` so in-place view→edit does not rebuild the block tree.
 */
export function getActionBuilderMetaDependencyKey(
  meta: Record<string, unknown> | undefined,
  blockMeta?: Record<string, unknown> | undefined,
): string {
  const merged = blockMeta != null ? { ...meta, ...blockMeta } : meta;
  if (merged == null) {
    return '';
  }
  const { mode: _mode, ...rest } = merged;
  return JSON.stringify(rest);
}

export interface FormBlock extends BaseBlock {
  $type: 'form';
  schema: { domain: string; orch: string; rules?: string };
  mode?: FormBlockMode;
  stepMode?: FormStepMode;
  /** Explicit entity ID — decouples the form from parent ControlRenderer's params prop. */
  entityId?: string;
  /** Pre-fetched data from a builder action — skips dataSource fetch when present. */
  data?: Record<string, unknown>;
  /** When true, suppresses both the Section header and the FormRenderer page shell title. */
  hideTitle?: boolean;
  /**
   * When true, the form does not render its own submit bar (footer with action buttons).
   * Use when submit is externally controlled — by a sibling ButtonBlock via effects,
   * a ModalSheet footer, or a WizardBlock step transition.
   */
  hideSubmitBar?: boolean;
  /** Called whenever form dirty state changes (e.g. user edits a field after submit). */
  onDirtyChange?: (isDirty: boolean) => void;
}

export interface StackBlock extends BaseBlock {
  $type: 'stack';
  blocks: AnyBlock[];
  gap?: string;
  /** Layout direction — `'column'` (default) or `'row'` for horizontal arrangement. */
  direction?: 'row' | 'column';
  /** Justify-content alignment along the main axis. */
  align?: 'start' | 'end' | 'between' | 'center';
  /** Enable flex-wrap for row direction. */
  wrap?: boolean;
  /** Stick to bottom of scroll container (e.g. for button bars). */
  sticky?: boolean;
  /** Render a top border separator above this stack. */
  divider?: boolean;
  /** Simple action ID inherited by all child view blocks. Auto-constructs `{ id: params.id }` params. */
  fetchActionId?: string;
  /** @deprecated Use `fetchActionId` instead. */
  dataSource?: ViewBlockDataSource;
  /** Cross-block effect declarations registered into the ambient EventScope. */
  effects?: import('@packages/block-events/core').EffectDeclaration[];
}

/**
 * ActionBlock — delegates to a builder action that returns a block tree with pre-fetched data.
 * The action receives params/meta from ControlRenderer automatically.
 *
 * `blockMeta` is merged into the execution context's `meta` before the nested
 * builder runs. This lets a parent builder configure a child builder without
 * polluting the ambient meta (e.g. passing `formMode: 'view'` or `hideTitle: true`).
 */
export interface ActionBlock extends BaseBlock {
  $type: 'action';
  actionId: string;
  /** Per-block config merged into `ctx.meta` when the nested builder executes. */
  blockMeta?: Record<string, unknown>;
}

/**
 * ButtonBlock — a self-contained button that emits a named event into the BlockEvents bus.
 *
 * The button knows nothing about forms, views, or tables. It is a pure event emitter.
 * Cross-block communication (e.g. triggering form submit, view refetch) is wired
 * through EffectDeclarations or `wire` on the target block — never on the button itself.
 */
export interface ButtonBlock extends BaseBlock {
  $type: 'button';
  label: string;
  variant?: 'primary' | 'primary-dark' | 'secondary' | 'secondary-gray' | 'secondary-dark' | 'link' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  icon?: string;
  trailingIcon?: string;
  /** The named event to emit into the EventBus on click. */
  event: string;
  /** Optional static payload attached to the emitted event. */
  eventPayload?: Record<string, unknown>;
  /**
   * Optional ActionRegistry action to execute on click (e.g. 'EXPORT_CSV', 'NAVIGATE_BACK').
   * Runs independently of the event — both fire if both are set.
   * The action receives the button's eventPayload as params.
   */
  actionId?: string;
  disabled?: boolean;
}

/**
 * SlotBlock — embeds an arbitrary React component inside the compose block tree.
 *
 * Use when an existing bespoke component (with its own hooks, props, and internal state)
 * needs to appear as a tab content or stack child inside a builder-action compose tree,
 * without being migrated to a named schema yet.
 *
 * The `render` function is called by `renderBlockTree` with no arguments. It must return
 * a valid React element. Keep it a thin closure — heavy logic stays in the component itself.
 *
 * Note: SlotBlock is intentionally NOT serialisable to JSON. It is only valid when returned
 * from a builder action at runtime (i.e. inside `BUILD_*` execute()). Never persist it.
 */
export interface SlotBlock extends BaseBlock {
  $type: 'slot';
  /** Factory that renders the component. Closed over any props it needs. */
  render: () => JSX.Element;
  /** Colocated wire declarations — collected by collectWireEffects at EventScopeBoundary. */
  wire?: import('@packages/block-events/core').BlockWire;
}

export type AnyBlock = FormBlock | TableBlock | ViewBlock | TabBlock | WizardBlock | StackBlock | ActionBlock | AccordionBlock | ButtonBlock | SlotBlock;

export interface ComposeSchema {
  $type: 'compose';
  $id: string;
  semanticId: string;
  root: string | AnyBlock;
  effects?: import('@packages/block-events/core').EffectDeclaration[];
}

// --- Resolved tree (all block refs materialized) ---

export interface ResolvedTabEntry {
  id: string;
  label: string;
  block: ResolvedAnyBlock;
  remountOnSwitch?: boolean;
}

export interface ResolvedWizardStep {
  id: string;
  label: string;
  block: ResolvedAnyBlock;
  transition?: StepTransition;
  guard?: WizardCondition;
}

export interface ResolvedTabBlock extends Omit<TabBlock, 'tabs'> {
  tabs: ResolvedTabEntry[];
}

export interface ResolvedWizardBlock extends Omit<WizardBlock, 'steps'> {
  steps: ResolvedWizardStep[];
}

export interface ResolvedStackBlock extends Omit<StackBlock, 'blocks'> {
  blocks: ResolvedAnyBlock[];
}

export interface ResolvedAccordionSection {
  id: string;
  label: string;
  block: ResolvedAnyBlock;
}

export interface ResolvedAccordionBlock extends Omit<AccordionBlock, 'sections'> {
  sections: ResolvedAccordionSection[];
}

export type ResolvedAnyBlock =
  | FormBlock
  | TableBlock
  | ViewBlock
  | ResolvedTabBlock
  | ResolvedWizardBlock
  | ResolvedStackBlock
  | ActionBlock
  | ResolvedAccordionBlock
  | ButtonBlock
  | SlotBlock;

export interface ResolvedComposeSchema extends Omit<ComposeSchema, 'root'> {
  root: ResolvedAnyBlock;
  effects?: import('@packages/block-events/core').EffectDeclaration[];
}

// --- Type guards ---

export function isFormBlock(b: AnyBlock): b is FormBlock {
  return b.$type === 'form';
}

export function isTableBlock(b: AnyBlock): b is TableBlock {
  return b.$type === 'table';
}

export function isViewBlock(b: AnyBlock): b is ViewBlock {
  return b.$type === 'view';
}

export function isTabBlock(b: AnyBlock): b is TabBlock {
  return b.$type === 'tab';
}

export function isWizardBlock(b: AnyBlock): b is WizardBlock {
  return b.$type === 'wizard';
}

export function isStackBlock(b: AnyBlock): b is StackBlock {
  return b.$type === 'stack';
}

export function isActionBlock(b: AnyBlock): b is ActionBlock {
  return b.$type === 'action';
}

export function isAccordionBlock(b: AnyBlock): b is AccordionBlock {
  return b.$type === 'accordion';
}

export function isButtonBlock(b: AnyBlock): b is ButtonBlock {
  return b.$type === 'button';
}

export function isComposeSchema(v: unknown): v is ComposeSchema {
  return typeof v === 'object' && v !== null && (v as ComposeSchema).$type === 'compose';
}

export function isAnyBlock(v: unknown): v is AnyBlock {
  if (typeof v !== 'object' || v === null) return false;
  const type = (v as AnyBlock).$type;
  return type === 'form' || type === 'table' || type === 'view' || type === 'tab' || type === 'wizard' || type === 'stack' || type === 'action' || type === 'accordion' || type === 'button';
}

// --- Wizard ↔ Form communication ---

export interface WizardFormHandle {
  validate: () => Promise<boolean>;
  getValues: () => Record<string, unknown>;
  submit: () => Promise<unknown>;
  isDirty: () => boolean;
  reset: (values?: Record<string, unknown>) => void;
  /** Subscribe to dirty-state changes. Called by FormRenderer's _setState effect. */
  onDirtyChange?: (isDirty: boolean) => void;
  hasMoreSteps?: () => boolean;
  advanceStep?: () => Promise<boolean>;
  canGoBackStep?: () => boolean;
  goBackStep?: () => void;
}

// --- Loader types ---

export interface RegisteredFormSchema {
  domain: Record<string, unknown>;
  orch: Record<string, unknown>;
  rules: Record<string, unknown>;
}

export interface RegisteredViewSchema {
  domain: Record<string, unknown>;
  orch: Record<string, unknown>;
}

export type ComposeLoader = (composeId: string) => Promise<ComposeSchema>;
export type FormSchemaLoader = (blockId: string) => Promise<RegisteredFormSchema | null>;
export type ViewSchemaLoader = (blockId: string) => Promise<RegisteredViewSchema | null>;
export type TableSchemaLoader = (blockId: string) => Promise<Record<string, unknown> | null>;

// --- Shared renderer props ---

export interface BlockRendererSharedProps {
  params?: Record<string, unknown>;
  meta?: Record<string, unknown>;
  onSuccess?: (data: unknown) => void;
  onError?: (err: unknown) => void;
  onCancel?: () => void;
  inheritedDataSource?: ViewBlockDataSource;
  /** ControlRenderer handle — forwarded to FormBlockRenderer to surface form state to parent. */
  handle?: import('../core/useControlRendererRef').ControlRendererHandle;
}

export type RenderBlockFn = (
  block: AnyBlock | ResolvedAnyBlock,
  shared: BlockRendererSharedProps,
) => JSX.Element;
